import { useCallback, useMemo } from "react";
import { useAuthenticatedApi } from "../auth";

export default function useFilesApi() {
  const { callProtectedApi, apiBaseUrl } = useAuthenticatedApi();

  const normalizeFilesPayload = useCallback((payload) => {
    if (!payload) return null;

    return {
      ...payload,
      documents: Array.isArray(payload.documents) ? payload.documents : [],
      references: Array.isArray(payload.references) ? payload.references : [],
      briefingMaterials: Array.isArray(payload.briefingMaterials)
        ? payload.briefingMaterials
        : [],
    };
  }, []);

  const getFilenameFromDisposition = useCallback((value) => {
    if (!value) return "";
    const utf8Match = value.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {
        return utf8Match[1];
      }
    }
    const asciiMatch = value.match(/filename\s*=\s*\"?([^\";]+)\"?/i);
    return asciiMatch?.[1] || "";
  }, []);

  const getStandardFiles = useCallback(
    async (standardId) => {
      if (!apiBaseUrl || !standardId) return null;

      const url = `${apiBaseUrl}/api/v1/files/references/${encodeURIComponent(standardId)}`;
      const res = await callProtectedApi(url, {
        headers: { "Content-Type": "application/json" },
      });
      return normalizeFilesPayload(res?.data ?? null);
    },
    [apiBaseUrl, callProtectedApi, normalizeFilesPayload],
  );

  const downloadBlobFromUrl = useCallback(
    async (url, onProgress) => {
      let simulatedProgress = 0;

      const res = await callProtectedApi(url, {
        method: "GET",
        responseType: "blob",

        onDownloadProgress: (e) => {
          if (!onProgress) return;

          if (e.lengthComputable && e.total) {
            const percent = Math.round((e.loaded * 100) / e.total);
            onProgress(percent);
          } else {
            // Simulated progress when the server cannot report total size.
            simulatedProgress += 5;
            if (simulatedProgress > 90) simulatedProgress = 90;
            onProgress(simulatedProgress);
          }
        },
      });

      // Ensure it completes to 100%
      if (onProgress) onProgress(100);

      const contentType = res?.headers?.["content-type"] || "";
      const disposition = res?.headers?.["content-disposition"] || "";
      const name = getFilenameFromDisposition(disposition) || "download";
      const blob = res?.data || null;

      return { blob, contentType, name };
    },
    [callProtectedApi, getFilenameFromDisposition],
  );

  const downloadFileBlob = useCallback(
    async (fileId, onProgress) => {
      if (!apiBaseUrl || !fileId) return null;

      const url = `${apiBaseUrl}/api/v1/files/download?id=${fileId}`;
      return downloadBlobFromUrl(url, onProgress);
    },
    [apiBaseUrl, downloadBlobFromUrl],
  );

  const deleteStandardFile = useCallback(
    async (fileId) => {
      if (!apiBaseUrl || !fileId) return false;

      const url = `${apiBaseUrl}/api/v1/files/${encodeURIComponent(fileId)}`;
      await callProtectedApi(url, {
        method: "DELETE",
      });

      return true;
    },
    [apiBaseUrl, callProtectedApi],
  );

  const archiveStandardFile = useCallback(
    async (fileId) => {
      if (!apiBaseUrl || !fileId) return false;

      const url = `${apiBaseUrl}/api/v1/files/archive?id=${fileId}`;
      await callProtectedApi(url, {
        method: "POST",
      });

      return true;
    },
    [apiBaseUrl, callProtectedApi],
  );

  const uploadDocumentFile = useCallback(
    async (standardId, file) => {
      if (!apiBaseUrl || !standardId || !file) return null;

      const url = `${apiBaseUrl}/api/v1/files/upload/document/${encodeURIComponent(standardId)}`;
      const form = new FormData();
      form.append("file", file);

      const res = await callProtectedApi(url, {
        method: "POST",
        data: form,
      });

      return res?.data ?? null;
    },
    [apiBaseUrl, callProtectedApi],
  );

  const uploadReferenceFile = useCallback(
    async (standardId, file) => {
      if (!apiBaseUrl || !standardId || !file) return null;

      const url = `${apiBaseUrl}/api/v1/files/upload/reference/${encodeURIComponent(standardId)}`;
      const form = new FormData();
      form.append("file", file);

      const res = await callProtectedApi(url, {
        method: "POST",
        data: form,
      });

      return res?.data ?? null;
    },
    [apiBaseUrl, callProtectedApi],
  );

  const uploadBriefingMaterialFile = useCallback(
    async (standardId, file) => {
      if (!apiBaseUrl || !standardId || !file) return null;

      const url = `${apiBaseUrl}/api/v1/files/upload/briefing/${encodeURIComponent(standardId)}`;
      const form = new FormData();
      form.append("file", file);

      const res = await callProtectedApi(url, {
        method: "POST",
        data: form,
      });

      return res?.data ?? null;
    },
    [apiBaseUrl, callProtectedApi],
  );

  return useMemo(
    () => ({
      getStandardFiles,
      downloadFileBlob,
      deleteStandardFile,
      archiveStandardFile,
      uploadDocumentFile,
      uploadReferenceFile,
      uploadBriefingMaterialFile,
    }),
    [
      getStandardFiles,
      downloadFileBlob,
      deleteStandardFile,
      archiveStandardFile,
      uploadDocumentFile,
      uploadReferenceFile,
      uploadBriefingMaterialFile,
    ],
  );
}
